<script setup lang="ts">
import { ref, useId, watch } from 'vue'
import { useTranslation } from 'i18next-vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { z } from 'zod'
import CardSelect from '@/components/ui/CardSelect.vue'
import { updateFormat } from '@/api/formatApi'
import type { OwnInstitutionFunction } from '@/types/formatInstitution.ts'

const ownInstitutionFunctionSchema = z.object({
    ownInstitutionFunction: z.enum(['CONDUCTED_BY', 'ORGANIZED_AND_CONDUCTED_BY'], {
        message: 'ui.institutions.validation.emptyFunction',
    }),
})

const visible = defineModel<boolean>('visible', { required: true })

const props = defineProps<{
    formatId: string
    currentValue?: OwnInstitutionFunction
}>()

const emit = defineEmits<{
    saved: []
}>()

const { t } = useTranslation()

const formId = `${useId()}-own-institution-function-form`
const descriptionId = `${useId()}-description`
const selectionErrorId = `${useId()}-selection-error`
const selected = ref<OwnInstitutionFunction | undefined>(props.currentValue)
const selectionError = ref('')
const saving = ref(false)

const options: { label: string; value: OwnInstitutionFunction }[] = [
    { label: 'ui.institutions.function.CONDUCTED_BY.label', value: 'CONDUCTED_BY' },
    {
        label: 'ui.institutions.function.ORGANIZED_AND_CONDUCTED_BY.label',
        value: 'ORGANIZED_AND_CONDUCTED_BY',
    },
]

watch(visible, (isVisible) => {
    if (isVisible) {
        selected.value = props.currentValue
        selectionError.value = ''
    }
})

watch(
    () => props.currentValue,
    (currentValue) => {
        if (visible.value && currentValue) selected.value = currentValue
    }
)

watch(selected, () => {
    selectionError.value = ''
})

const save = async () => {
    const result = ownInstitutionFunctionSchema.safeParse({
        ownInstitutionFunction: selected.value,
    })
    if (!result.success) {
        selectionError.value = t(result.error.issues[0].message)
        return
    }

    saving.value = true
    try {
        await updateFormat(props.formatId, {
            ownInstitutionFunction: result.data.ownInstitutionFunction,
        })
        emit('saved')
        visible.value = false
    } finally {
        saving.value = false
    }
}
</script>

<template>
    <Dialog
        v-model:visible="visible"
        modal
        :closable="!saving"
        class="w-modal-md"
        :header="t('ui.institutions.ownFunctionModal.title')"
        :aria-describedby="descriptionId"
    >
        <form
            :id="formId"
            data-testid="own-institution-function-modal-form"
            class="space-y-6"
            @submit.prevent="save"
        >
            <p :id="descriptionId" class="paragraph-sm text-neutral-600">
                {{ t('ui.institutions.ownFunctionModal.description') }}
            </p>

            <fieldset class="space-y-3">
                <legend class="sr-only">
                    {{ t('ui.institutions.ownFunctionModal.title') }}
                </legend>
                <CardSelect
                    v-model:single-value="selected"
                    :options
                    single
                    dense
                    cols-class="grid-cols-1"
                    :aria-describedby="
                        selectionError ? `${descriptionId} ${selectionErrorId}` : descriptionId
                    "
                    :aria-invalid="!!selectionError"
                />

                <p
                    v-if="selectionError"
                    :id="selectionErrorId"
                    class="text-sm text-danger"
                    role="alert"
                >
                    {{ selectionError }}
                </p>

                <p v-if="selected === 'CONDUCTED_BY'" class="text-xs text-neutral-600">
                    {{ t('ui.institutions.ownFunctionModal.conductedByHint') }}
                </p>
            </fieldset>
        </form>

        <template #footer>
            <div class="flex w-full justify-end">
                <Button
                    type="submit"
                    :form="formId"
                    :label="t('ui.institutions.ownFunctionModal.submitLabel')"
                    :loading="saving"
                    data-testid="own-institution-function-modal-submit-button"
                />
            </div>
        </template>
    </Dialog>
</template>
